import type { ChatMessages } from "@storytell/cp-client";
import type { WebSearchResultsCollection } from "./citationResolver";

/**
 * Normalizes tool ID to handle casing differences between backend and frontend
 * Backend generates web_toolExecution_* (camelCase) but frontend expects web_toolexecution_* (lowercase)
 *
 * @param toolId - The original tool ID
 * @returns Normalized tool ID in lowercase format
 */
const normalizeToolId = (toolId: string): string => {
  // Convert web_toolExecution_* to web_toolexecution_* for compatibility
  if (toolId.startsWith("web_toolExecution_")) {
    return toolId.replace("web_toolExecution_", "web_toolexecution_");
  }
  // Already lowercase, return as-is
  return toolId;
};

/**
 * Builds a WebSearchResultsCollection from an array of WebSearchResult objects
 * for efficient lookup by tool ID. Handles toolId casing conversion for compatibility.
 *
 * @param webSearchResults - Array of WebSearchResult objects from protobuf
 * @returns Collection organized by normalized tool ID for O(1) lookups
 */
export const buildWebSearchCollection = (
  webSearchResults: ChatMessages.WebSearchResult[] | undefined,
): WebSearchResultsCollection => {
  const byToolId = new Map<string, ChatMessages.WebSearchResult>();

  if (!webSearchResults) {
    return { byToolId };
  }

  // Index each WebSearchResult by its normalized tool_id
  for (const result of webSearchResults) {
    if (result.toolId) {
      const normalizedToolId = normalizeToolId(result.toolId);
      byToolId.set(normalizedToolId, result);
    }
  }

  return { byToolId };
};

/**
 * Builds a WebSearchResultsCollection from a single WebSearchResult object
 * for backward compatibility with existing single-result patterns. Handles toolId casing conversion.
 *
 * @param webSearchResult - Single WebSearchResult object
 * @returns Collection with the single result indexed by its normalized tool ID
 */
export const buildWebSearchCollectionFromSingle = (
  webSearchResult: ChatMessages.WebSearchResult | undefined,
): WebSearchResultsCollection => {
  const byToolId = new Map<string, ChatMessages.WebSearchResult>();

  if (webSearchResult?.toolId) {
    const normalizedToolId = normalizeToolId(webSearchResult.toolId);
    byToolId.set(normalizedToolId, webSearchResult);
  }

  return { byToolId };
};

/**
 * Merges multiple WebSearchResultsCollections into a single collection
 * Useful when aggregating results from multiple sources or operations
 *
 * @param collections - Array of collections to merge
 * @returns Merged collection containing all results
 */
export const mergeWebSearchCollections = (
  collections: (WebSearchResultsCollection | undefined)[],
): WebSearchResultsCollection => {
  const byToolId = new Map<string, ChatMessages.WebSearchResult>();

  for (const collection of collections) {
    if (collection) {
      for (const [toolId, result] of collection.byToolId) {
        byToolId.set(toolId, result);
      }
    }
  }

  return { byToolId };
};

/**
 * Gets all tool IDs available in a collection
 *
 * @param collection - The collection to get tool IDs from
 * @returns Array of tool IDs
 */
export const getAvailableToolIds = (collection: WebSearchResultsCollection | undefined): string[] => {
  if (!collection) return [];
  return Array.from(collection.byToolId.keys());
};

/**
 * Builds a WebSearchResultsCollection from multiple individual WebSearchResult objects
 * This is the primary function for the actual data structure from debug recordings
 *
 * @param webSearchResults - Array of individual WebSearchResult objects from different blocks
 * @returns Collection organized by normalized tool ID for O(1) lookups
 */
export const buildWebSearchCollectionFromMultiple = (
  webSearchResults: ChatMessages.WebSearchResult[],
): WebSearchResultsCollection => {
  return buildWebSearchCollection(webSearchResults);
};

/**
 * Gets the total number of web search results across all tool IDs
 *
 * @param collection - The collection to count results from
 * @returns Total number of individual search result items
 */
export const getTotalResultCount = (collection: WebSearchResultsCollection | undefined): number => {
  if (!collection) return 0;

  let count = 0;
  for (const result of collection.byToolId.values()) {
    count += result.results?.length ?? 0;
  }
  return count;
};
